/**
 * Single-entry point for the framework-agnostic mount pipeline.
 *
 * Framework modules call `bootstrapFrameworkMount(adapter)`
 * from their entry script to wire everything up: the mount
 * registry, the behavior installer, the lazy observer, the
 * coordinator, and the crash reporter. The returned bootstrap
 * object exposes the registry and coordinator so the entry
 * script can register side effects like
 * `Drupal.sdxNavigation.registerComponentResolver` without
 * reaching into the core's internals.
 *
 * Everything downstream of this file is internal to
 * `@sdx/core/mount/*`; framework modules only interact with
 * the adapter contract in `./types` and this bootstrap
 * function.
 */

import { bootstrapMount, type MountBootstrap } from './behavior-installer';
import { MountRegistry } from './mount-registry';
import type { FrameworkMountAdapter } from './types';

export type { FrameworkMountAdapter, MountBootstrap };
export type {
  FrameworkSettings,
  MountInstance,
  MountParams,
  MountRegistries,
} from './types';
export { MountRegistry } from './mount-registry';
export { createNavigationComponentResolver, resolveComponentPath } from './component-resolver';
export { ensureDevClient, type DevClientOptions } from './dev-client';

/**
 * Constructs the mount pipeline for the given adapter and
 * installs its Drupal behavior. Returns the bootstrap object
 * so callers can attach side effects like navigation
 * component resolvers.
 */
export function bootstrapFrameworkMount<Instance, RuntimeModule>(
  adapter: FrameworkMountAdapter<Instance, RuntimeModule>,
): MountBootstrap<Instance, RuntimeModule> {
  const registry = new MountRegistry<Instance>();
  return bootstrapMount(adapter, registry);
}
